import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Shield, Users, AlertTriangle, DollarSign, MapPin, XCircle, Truck, Link } from 'lucide-react';
import api from '../utils/api';
import { format } from 'date-fns';
import MapComponent from '../components/MapComponent';
import DataGrid from '../components/DataGrid';
import StatsCard from '../components/StatsCard';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Add this configuration object for payment statuses
const PAYMENT_STATUS_CONFIG = {
  'under_review': {
    label: 'Under Review',
    color: 'yellow',
    action: 'Review'
  },
  'payment_marked': {
    label: 'Payment Marked',
    color: 'blue',
    action: 'Verify'
  },
  'payment_verified': {
    label: 'Payment Verified',
    color: 'green',
    action: 'Release'
  }
};

const AdminDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingUsers: 0,
    activeUsers: 0,
    disputedPayments: 0,
    totalLocations: 0,
    samsaraCarriers: 0
  });
  const [pendingUsers, setPendingUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [disputedPayments, setDisputedPayments] = useState([]);
  const [userLocations, setUserLocations] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [activeUsers, setActiveUsers] = useState({ 
    users: [], 
    pagination: {
      total: 0,
      pages: 0,
      current_page: 1,
      per_page: 50
    } 
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [disputedTransactions, setDisputedTransactions] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'carrier', 'customer'
  const [samsaraCarriers, setSamsaraCarriers] = useState({
    carriers: [],
    pagination: {
      total: 0,
      pages: 0,
      current_page: 1,
      per_page: 50
    }
  });
  const [samsaraStatusFilter, setSamsaraStatusFilter] = useState('active'); // 'active', 'all'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch base data that's always needed
        const [
          statsResponse,
          pendingUsersResponse,
          activityResponse,
          paymentsResponse,
          locationsResponse
        ] = await Promise.all([
          api.get('/api/admin/dashboard/stats'),
          api.get('/api/admin/users/pending'),
          api.get('/api/admin/activity/recent'),
          api.get('/api/admin/payments'),
          api.get('/api/admin/location-tracking', {
            params: {
              per_page: 1000,
              sort_by: 'timestamp',
              sort_dir: 'desc'
            }
          })
        ]);

        setStats(statsResponse.data);
        setPendingUsers(pendingUsersResponse.data);
        setRecentActivity(activityResponse.data);
        setDisputedPayments(paymentsResponse.data);
        setUserLocations(locationsResponse.data.locations);
        
        // Fetch tab-specific data separately
        if (selectedTab === 'active') {
          const activeUsersResponse = await api.get('/api/admin/users/active', { params: { user_type: userTypeFilter } });
          setActiveUsers(activeUsersResponse.data);
        }

        if (selectedTab === 'disputes') {
          const disputedTransactionsResponse = await api.get('/api/admin/billings/disputed');
          setDisputedTransactions(disputedTransactionsResponse.data);
        }

        if (selectedTab === 'samsara') {
          const samsaraCarriersResponse = await api.get('/api/admin/carriers/samsara', { params: { status: samsaraStatusFilter } });
          setSamsaraCarriers(samsaraCarriersResponse.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedTab, userTypeFilter, samsaraStatusFilter]);

  const handleApproveUser = async (userId) => {
    try {
      // Call the correct approval endpoint
      await api.post(`/approve/${userId}`);
      
      // Show success notification
      toast.success('User approved successfully', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Refresh the pending users list
      const response = await api.get('/api/admin/users/pending');
      setPendingUsers(response.data);
      
      // Refresh stats to update pending users count
      const statsResponse = await api.get('/api/admin/dashboard/stats');
      setStats(statsResponse.data);
    } catch (error) {
      // Handle specific error cases
      const errorMessage = error.response?.data?.message || 'Failed to approve user';
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error approving user:', error);
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/reject`);
      
      // Show success notification
      toast.success('User rejected successfully', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Refresh the pending users list
      const response = await api.get('/api/admin/users/pending');
      setPendingUsers(response.data);
      
      // Refresh stats to update pending users count
      const statsResponse = await api.get('/api/admin/dashboard/stats');
      setStats(statsResponse.data);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reject user';
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error rejecting user:', error);
    }
  };

  const handleReviewPayment = async (paymentId) => {
    try {
      // Navigate to payment review page
      navigate(`/admin/payments/${paymentId}/review`);
    } catch (error) {
      console.error('Error reviewing payment:', error);
    }
  };

  const handleDisputeAction = async (transactionId, action, resolution) => {
    try {
      await api.post(`/api/billings/${transactionId}/action`, {
        action,
        resolution,
        comments: resolution // Using resolution as comments for audit trail
      });

      // Refresh disputed transactions
      const response = await api.get('/api/admin/billings/disputed');
      setDisputedTransactions(response.data);

      // Refresh stats
      const statsResponse = await api.get('/api/admin/dashboard/stats');
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error handling dispute:', error);
      // Add proper error handling/notification here
    }
  };

  const handlePaymentAction = async (payment, action) => {
    try {
      // Map the UI actions to backend action names
      const actionMap = {
        'Review': 'review_dispute',
        'Verify': 'verify_payment',
        'Release': 'release_payment'
      };

      const backendAction = actionMap[action];
      if (!backendAction) {
        throw new Error('Invalid action');
      }

      // Use UUID for API calls, fall back to id for backwards compatibility
      const paymentIdentifier = payment.uuid || payment.id;
      await api.post(`/api/billings/${paymentIdentifier}/action`, {
        action: backendAction,
        comments: `Admin ${action.toLowerCase()}ed payment`
      });

      // Show success toast
      toast.success(`Payment ${action.toLowerCase()}ed successfully`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Refresh the payments list
      const response = await api.get('/api/admin/payments');
      setDisputedPayments(response.data);

    } catch (error) {
      console.error(`Error ${action.toLowerCase()}ing payment:`, error);
      toast.error(error.response?.data?.error || `Failed to ${action.toLowerCase()} payment`, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  const statsCards = [
    {
      title: 'Pending Approvals',
      value: stats.pendingUsers,
      icon: Users,
      theme: 'blue',
      subtitle: 'Awaiting review',
      onClick: () => {
        setSelectedTab('pending');
        setMobileMenuOpen(false);
      }
    },
    {
      title: 'Disputed Payments',
      value: stats.disputedPayments,
      icon: DollarSign,
      theme: 'amber',
      subtitle: 'Needs attention',
      onClick: () => setSelectedTab('payments')
    },
    {
      title: 'Active Users',
      value: stats.activeUsers,
      icon: Shield,
      theme: 'emerald',
      subtitle: 'Verified accounts',
      onClick: () => setSelectedTab('active')
    },
    {
      title: 'Samsara Connected',
      value: stats.samsaraCarriers,
      icon: Link,
      theme: 'violet',
      subtitle: 'Telematics enabled',
      onClick: () => setSelectedTab('samsara')
    },
    {
      title: 'Location Events',
      value: stats.totalLocations,
      icon: MapPin,
      theme: 'rose',
      subtitle: 'Total tracked',
      onClick: () => setSelectedTab('locations')
    }
  ];

  const renderContent = () => {
    switch (selectedTab) {
      case 'pending':
        return (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Pending Approvals</h3>
                  <p className="text-sm text-slate-500">Users awaiting verification</p>
                </div>
              </div>
            </div>
            <DataGrid
              data={pendingUsers}
              columns={[
                { 
                  field: 'email', 
                  header: 'Email',
                  render: (row) => (
                    <span className="font-medium text-slate-900">{row.email}</span>
                  )
                },
                { 
                  field: 'userType', 
                  header: 'User Type',
                  render: (row) => (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                      row.userType?.includes('carrier') 
                        ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' 
                        : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                    }`}>
                      {row.userType?.replace('_', ' ') || 'N/A'}
                    </span>
                  )
                },
                { field: 'createdAt', header: 'Registration Date',
                  render: (row) => {
                    const d = row.createdAt ? new Date(row.createdAt) : null;
                    return (
                      <span className="text-slate-600">
                        {d && !isNaN(d.getTime()) ? format(d, 'MMM dd, yyyy') : 'N/A'}
                      </span>
                    );
                  } 
                },
                { field: 'actions', header: 'Actions',
                  render: (row) => (
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleApproveUser(row.id)}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors ring-1 ring-emerald-600/20"
                      >
                        Approve
                      </button>
                      <button 
                        onClick={() => handleRejectUser(row.id)}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors ring-1 ring-red-600/20"
                      >
                        Reject
                      </button>
                    </div>
                  )
                }
              ]}
            />
          </div>
        );

      case 'payments':
        return (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Payment Review Queue</h3>
                  <p className="text-sm text-slate-500">Transactions requiring attention</p>
                </div>
              </div>
            </div>
            <DataGrid
              data={disputedPayments}
              columns={[
                { 
                  field: 'uuid', 
                  header: 'Transaction ID',
                  render: (row) => (
                    <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded text-slate-700" title={row.uuid}>
                      {row.uuid ? row.uuid.substring(0, 8).toUpperCase() : 'N/A'}
                    </span>
                  )
                },
                { 
                  field: 'loadUuid', 
                  header: 'Load ID',
                  render: (row) => (
                    <span className="font-mono text-sm text-slate-600" title={row.loadUuid}>
                      {row.loadUuid ? row.loadUuid.substring(0, 8).toUpperCase() : 'N/A'}
                    </span>
                  )
                },
                { 
                  field: 'amount', 
                  header: 'Amount',
                  render: (row) => (
                    <span className="font-semibold text-slate-900">${row.amount.toFixed(2)}</span>
                  )
                },
                { field: 'customerName', header: 'Customer' },
                { field: 'carrierName', header: 'Carrier' },
                {
                  field: 'status',
                  header: 'Status',
                  render: (row) => {
                    const statusStyles = {
                      yellow: 'bg-amber-50 text-amber-700 ring-amber-600/20',
                      blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
                      green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                    };
                    const config = PAYMENT_STATUS_CONFIG[row.status];
                    return (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ring-1 ${statusStyles[config?.color] || statusStyles.blue}`}>
                        {config?.label || row.status}
                      </span>
                    );
                  }
                },
                {
                  field: 'actions',
                  header: 'Actions',
                  render: (row) => {
                    const config = PAYMENT_STATUS_CONFIG[row.status];
                    const buttonStyles = {
                      yellow: 'bg-amber-600 hover:bg-amber-700 text-white',
                      blue: 'bg-blue-600 hover:bg-blue-700 text-white',
                      green: 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    };
                    return (
                      <button
                        onClick={() => config.action === 'Review' ? 
                          handleReviewPayment(row.uuid) : 
                          handlePaymentAction(row, config.action)
                        }
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${buttonStyles[config?.color] || buttonStyles.blue}`}
                      >
                        {config?.action || 'View'}
                      </button>
                    );
                  }
                }
              ]}
            />
          </div>
        );

      case 'locations':
        return (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Location Tracking</h3>
                  <p className="text-sm text-slate-500">Real-time user activity map</p>
                </div>
              </div>
            </div>
            <div className="h-[600px]">
              <MapComponent
                locations={userLocations}
                clustered={true}
                showInfo={true}
                isUserLocation={true}
              />
            </div>
          </div>
        );

      case 'active':
        return (
          <div className="space-y-5">
            {/* User Type Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-200/50">
              <span className="text-sm font-medium text-slate-600">Filter by type:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Users', icon: Users },
                  { value: 'carrier', label: 'Carriers', icon: Truck },
                  { value: 'customer', label: 'Customers', icon: Users }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setUserTypeFilter(option.value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      userTypeFilter === option.value
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <option.icon className="w-4 h-4" />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="text-lg font-semibold text-slate-900">
                  {userTypeFilter === 'all' ? 'All Active Users' : userTypeFilter === 'carrier' ? 'Active Carriers' : 'Active Customers'}
                </h3>
                <p className="text-sm text-slate-500">Approved and verified accounts</p>
              </div>
              <DataGrid
                data={activeUsers?.users || []}
                columns={[
                  { field: 'email', header: 'Email' },
                  { field: 'userType', header: 'User Type',
                    render: (row) => (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                        row.userType.includes('carrier') 
                          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' 
                          : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                      }`}>
                        {row.userType.replace('_', ' ')}
                      </span>
                    )
                  },
                  { 
                    field: 'name', 
                    header: 'Name',
                    render: (row) => (
                      <span className="font-medium text-slate-900">{row.firstName} {row.lastName}</span>
                    )
                  },
                  { field: 'company', header: 'Company' },
                  { 
                    field: 'lastLogin', 
                    header: 'Last Login',
                    render: (row) => (
                      <span className="text-slate-600">
                        {row.lastLogin ? format(new Date(row.lastLogin), 'MMM dd, yyyy HH:mm') : 'Never'}
                      </span>
                    )
                  },
                  { field: 'lastLoginLocation', header: 'Last Location' },
                  { field: 'loginCount', header: 'Logins' },
                  {
                    field: 'createdAt',
                    header: 'Member Since',
                    render: (row) => format(new Date(row.createdAt), 'MMM dd, yyyy')
                  }
                ]}
                pagination={activeUsers.pagination}
                onPageChange={(page) => {
                  api.get('/api/admin/users/active', { params: { page, user_type: userTypeFilter } })
                    .then(response => setActiveUsers(response.data))
                    .catch(error => console.error('Error fetching active users:', error));
                }}
              />
            </div>
          </div>
        );

      case 'samsara':
        return (
          <div className="space-y-5">
            {/* Samsara Status Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-200/50">
              <span className="text-sm font-medium text-slate-600">Integration status:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'active', label: 'Active Only' },
                  { value: 'all', label: 'All Statuses' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSamsaraStatusFilter(option.value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      samsaraStatusFilter === option.value
                        ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Link className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Samsara Integrations</h3>
                    <p className="text-sm text-slate-500">Carriers with telematics connected</p>
                  </div>
                </div>
              </div>
              <DataGrid
                data={samsaraCarriers?.carriers || []}
                columns={[
                  { 
                    field: 'companyName', 
                    header: 'Company Name',
                    render: (row) => (
                      <span className="font-medium text-slate-900">{row.companyName}</span>
                    )
                  },
                  { 
                    field: 'mcNumber', 
                    header: 'MC Number',
                    render: (row) => (
                      <span className="font-mono text-sm text-slate-600">{row.mcNumber || '—'}</span>
                    )
                  },
                  { 
                    field: 'dotNumber', 
                    header: 'DOT Number',
                    render: (row) => (
                      <span className="font-mono text-sm text-slate-600">{row.dotNumber || '—'}</span>
                    )
                  },
                  { 
                    field: 'samsara.orgName', 
                    header: 'Samsara Org',
                    render: (row) => row.samsara?.orgName || '—'
                  },
                  { 
                    field: 'samsara.status', 
                    header: 'Status',
                    render: (row) => {
                      const status = row.samsara?.status;
                      const statusStyles = {
                        active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
                        pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
                        expired: 'bg-orange-50 text-orange-700 ring-orange-600/20',
                        revoked: 'bg-red-50 text-red-700 ring-red-600/20',
                        error: 'bg-red-50 text-red-700 ring-red-600/20'
                      };
                      return (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ring-1 ${statusStyles[status] || 'bg-slate-50 text-slate-700 ring-slate-600/20'}`}>
                          {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
                        </span>
                      );
                    }
                  },
                  { 
                    field: 'samsara.connectedAt', 
                    header: 'Connected',
                    render: (row) => (
                      <span className="text-slate-600">
                        {row.samsara?.connectedAt 
                          ? format(new Date(row.samsara.connectedAt), 'MMM dd, yyyy') 
                          : '—'}
                      </span>
                    )
                  },
                  { 
                    field: 'samsara.lastSyncedAt', 
                    header: 'Last Synced',
                    render: (row) => (
                      <span className="text-slate-600">
                        {row.samsara?.lastSyncedAt 
                          ? format(new Date(row.samsara.lastSyncedAt), 'MMM dd, HH:mm') 
                          : '—'}
                      </span>
                    )
                  }
                ]}
                pagination={samsaraCarriers.pagination}
                onPageChange={(page) => {
                  api.get('/api/admin/carriers/samsara', { params: { page, status: samsaraStatusFilter } })
                    .then(response => setSamsaraCarriers(response.data))
                    .catch(error => console.error('Error fetching Samsara carriers:', error));
                }}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {statsCards.map((card, index) => (
                <StatsCard key={index} {...card} />
              ))}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DataGrid
                title="Recent Activity"
                data={recentActivity.slice(0, 5)}
                columns={[
                  { field: 'timestamp', header: 'Time',
                    render: (row) => {
                      // Add validation before formatting
                      const timestamp = new Date(row.timestamp);
                      return isNaN(timestamp.getTime()) 
                        ? 'Invalid date'
                        : format(timestamp, 'MMM dd, HH:mm');
                    }
                  },
                  { field: 'action', header: 'Action' },
                  { field: 'userEmail', header: 'User' },
                  { field: 'details', header: 'Details' }
                ]}
              />
              
              <div className="h-[400px]">
                <MapComponent
                  locations={userLocations.slice(0, 100)}
                  clustered={true}
                  showInfo={true}
                  isUserLocation={true}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const TAB_CONFIG = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'pending', label: 'Pending', icon: Users },
    { id: 'active', label: 'Active Users', icon: Shield },
    { id: 'samsara', label: 'Samsara', icon: Link },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'locations', label: 'Locations', icon: MapPin }
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 p-3 pt-20 sm:p-6 sm:pt-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-indigo-600 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Admin Console</h1>
            </div>
            <p className="text-slate-500 ml-12 sm:ml-14">
              Welcome back, <span className="font-medium text-slate-700">{user.firstName}</span>
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-6 relative">
            {/* Mobile dropdown */}
            <div className="sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-200"
              >
                <span className="font-medium text-slate-700 capitalize">{selectedTab}</span>
                <svg className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`} 
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {mobileMenuOpen && (
                <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                  {TAB_CONFIG.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setSelectedTab(tab.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                        selectedTab === tab.id
                          ? 'bg-indigo-50 text-indigo-600 border-l-4 border-indigo-600'
                          : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop tabs */}
            <nav className="hidden sm:flex items-center gap-1 p-1.5 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`px-4 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 transition-all duration-200 ${
                    selectedTab === tab.id
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="space-y-6">
            {selectedTab === 'overview' && (
              <>
                {/* Stats Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5">
                  {statsCards.map((card, index) => (
                    <StatsCard key={index} {...card} />
                  ))}
                </div>
                
                {/* Activity & Map Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
                      <p className="text-sm text-slate-500">Latest system events</p>
                    </div>
                    <div className="p-4">
                      <DataGrid
                        data={recentActivity.slice(0, 5)}
                        columns={[
                          { field: 'timestamp', header: 'Time',
                            render: (row) => {
                              const timestamp = new Date(row.timestamp);
                              return isNaN(timestamp.getTime()) 
                                ? 'Invalid date'
                                : format(timestamp, 'MMM dd, HH:mm');
                            }
                          },
                          { field: 'action', header: 'Action' },
                          { field: 'userEmail', header: 'User' },
                          { field: 'details', header: 'Details' }
                        ]}
                        compact
                      />
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <h3 className="text-lg font-semibold text-slate-900">Location Overview</h3>
                      <p className="text-sm text-slate-500">User activity map</p>
                    </div>
                    <div className="h-[380px]">
                      <MapComponent
                        locations={userLocations.slice(0, 100)}
                        clustered={true}
                        showInfo={true}
                        isUserLocation={true}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {(selectedTab === 'pending' || selectedTab === 'payments' || selectedTab === 'active' || selectedTab === 'samsara') && (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <div className="min-w-[800px] px-3 sm:px-0">
                  {renderContent()}
                </div>
              </div>
            )}

            {selectedTab === 'locations' && renderContent()}
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastClassName="!rounded-xl !shadow-lg"
      />
    </>
  );
};

export default AdminDashboard; 